/* CameraAvatarPicker — التِقاط صورة مباشرة من الكاميرا، ثُمَّ تَحويلها
   server-side إلى أَفتار AI مُسَتَيلَز يَحفَظ الهُويّة لكن ليس فوتوغرافيّاً.

   فلسفة الخصوصية:
   - الكاميرا فقط (لا upload من المعرض) → يَضمن أنّ الصورة فعلاً للمحامي.
   - الصورة الأصليّة لا تَترك جهاز المحامي حتى يَضغط "استخدام هذا الأَفتار".
   - السيرفر يُمَرِّرها لـ Nano Banana، يَستَلم الأَفتار، ثم يَحذف الأصليّة فوراً.
   - لا يُحفَظ في DB إلا الأَفتار المُسَتَيلَز.
*/
import { useEffect, useRef, useState } from "react";
import { Camera, X, ArrowClockwise, Check, Sparkle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

// 1024 max side keeps upload < 1 MB while giving Nano Banana enough
// detail to preserve identity.
const CAPTURE_SIZE = 1024;

async function startCamera(videoEl) {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
    audio: false,
  });
  videoEl.srcObject = stream;
  await videoEl.play();
  return stream;
}

function captureSquarePNG(videoEl) {
  // Centre-square crop at the native video resolution, then downsample
  // smoothly to CAPTURE_SIZE so the upload payload stays small but the
  // AI still receives sharp facial features.
  // The capture is HORIZONTALLY MIRRORED to match the front-camera preview
  // (which we flip with `scaleX(-1)` below) so the user sees the exact
  // framing they captured — same as Instagram / Snapchat / iOS Camera.
  const native = Math.min(videoEl.videoWidth, videoEl.videoHeight);
  const ox = (videoEl.videoWidth - native) / 2;
  const oy = (videoEl.videoHeight - native) / 2;

  const c = document.createElement("canvas");
  c.width = CAPTURE_SIZE;
  c.height = CAPTURE_SIZE;
  const ctx = c.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // Flip horizontally so the captured frame matches the mirrored preview.
  ctx.translate(CAPTURE_SIZE, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(videoEl, ox, oy, native, native, 0, 0, CAPTURE_SIZE, CAPTURE_SIZE);
  return new Promise((resolve) => c.toBlob((b) => resolve(b), "image/png", 0.95));
}

export default function CameraAvatarPicker({ open, onClose, onCaptured }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState("ready"); // ready | preview | sending
  const [previewUrl, setPreviewUrl] = useState("");
  const previewBlobRef = useRef(null);

  const stopCam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const init = async () => {
      try {
        if (!videoRef.current) return;
        streamRef.current = await startCamera(videoRef.current);
        if (!alive) stopCam();
      } catch {
        toast.error("تَعذّر فَتح الكاميرا — تَأكَّد من السَّماح بالوصول");
        onClose && onClose();
      }
    };
    init();
    return () => { alive = false; stopCam(); };
  }, [open, onClose]);

  const handleCapture = async () => {
    if (!videoRef.current) return;
    const blob = await captureSquarePNG(videoRef.current);
    previewBlobRef.current = blob;
    setPreviewUrl(URL.createObjectURL(blob));
    setPhase("preview");
    stopCam();
  };

  const handleRetake = async () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    previewBlobRef.current = null;
    setPhase("ready");
    if (videoRef.current) {
      try { streamRef.current = await startCamera(videoRef.current); }
      catch { toast.error("تَعذّر فَتح الكاميرا"); }
    }
  };

  const handleConfirm = async () => {
    if (!previewBlobRef.current) return;
    setPhase("sending");
    try {
      // The parent (PublicPresenceSection) does the actual upload via
      // `uploadAvatar()`. We hand it the raw selfie blob; the server
      // converts it to AI avatar and never persists the original.
      await Promise.resolve(onCaptured(previewBlobRef.current));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    } catch (e) {
      // Parent will show its own error toast; we just unstick the modal.
      setPhase("preview");
      throw e;
    }
  };

  if (!open) return null;

  const isSending = phase === "sending";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 font-tajawal"
      style={{ background: "rgba(10,25,47,0.85)", backdropFilter: "blur(8px)" }}
      dir="rtl"
    >
      <div className="bg-white rounded-[28px] w-full max-w-md overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-[#EAEAEA]">
          <h3 className="font-bold text-[16px] text-[#0A192F]">صورة الأَفتار</h3>
          <button
            type="button"
            onClick={() => { stopCam(); onClose && onClose(); }}
            aria-label="إغلاق"
            data-testid="avatar-close"
            disabled={isSending}
            className="w-9 h-9 rounded-full bg-[#F3F3F3] hover:bg-[#EAEAEA] flex items-center justify-center disabled:opacity-40"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <p className="text-[12.5px] text-[#5A6470] leading-relaxed mb-3 text-center">
            <Sparkle size={14} weight="fill" className="inline-block text-[#D4AF37] -mt-1" />{" "}
            🔒 لأَسباب الخصوصية، يَتِم تَحويل صورتك تلقائياً إلى{" "}
            <b>أَفتار رَسم احترافي بالذَّكاء الاصطناعي</b> يُشبِهك دون أن يَكون صورتك الحقيقيّة.
            الصورة الأصليّة لا تُحفَظ.
          </p>

          <div className="relative aspect-square w-full max-w-[300px] mx-auto rounded-full overflow-hidden bg-black ring-4 ring-[#D4AF37]/40">
            {phase === "ready" ? (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                style={{ transform: "scaleX(-1)" }}
                muted
                playsInline
                data-testid="avatar-video"
              />
            ) : (
              <>
                <img
                  src={previewUrl}
                  alt="أَفتار"
                  className="w-full h-full object-cover"
                  data-testid="avatar-preview"
                />
                {isSending && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 text-white text-center px-4">
                    <CircleNotch size={36} className="animate-spin text-[#D4AF37]" />
                    <div className="font-bold text-[14px]">جاري توليد الأَفتار...</div>
                    <div className="text-[11px] opacity-80">
                      الذَّكاء الاصطناعي يَرسم نُسخة فنّيّة منك — قَد يَستَغرِق 10-20 ثانية.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div className="mt-5 flex items-center justify-center gap-3">
            {phase === "ready" ? (
              <button
                type="button"
                onClick={handleCapture}
                data-testid="avatar-capture"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A192F] text-white text-[14px] font-bold active:scale-95 transition shadow-[0_6px_18px_rgba(10,25,47,0.3)]"
              >
                <Camera size={18} weight="fill" />
                التِقاط
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleRetake}
                  data-testid="avatar-retake"
                  disabled={isSending}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#EAEAEA] text-[#0A192F] text-[14px] font-bold active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowClockwise size={16} weight="bold" />
                  إعادة
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  data-testid="avatar-confirm"
                  disabled={isSending}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-[#E6C463] to-[#B8860B] text-white text-[14px] font-bold active:scale-95 transition shadow-[0_6px_18px_rgba(212,175,55,0.45)] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSending ? (
                    <>
                      <CircleNotch size={16} className="animate-spin" />
                      توليد...
                    </>
                  ) : (
                    <>
                      <Sparkle size={16} weight="fill" />
                      توليد الأَفتار بالـ AI
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          <p className="mt-3 text-[10.5px] text-[#8A8A8A] text-center leading-relaxed">
            💡 لديك <b>5 محاولات شَهريّاً</b>. اختَر أَفضَل نَتيجة.
          </p>
        </div>
      </div>
    </div>
  );
}
